// src/app/components/analytics/analytics-overview.tsx
import { IncompleteRunNotice } from "@/app/components/shared/incomplete-run-notice";
import { RebaselineNotice } from "@/app/components/shared/rebaseline-notice";
import { useState, useEffect } from "react";
import {
  BarChart3, TrendingUp, Clock, Sparkles,
  ChevronDown,
} from "lucide-react";
import { ModuleHeader } from "../shell/module-header";
import { AIAssistantSidebar }          from "../ai-assistant-sidebar";
import { PerceptionScoreChart }        from "./perception-score-chart";
import { ReferralConversationsChart }  from "./referral-conversations-chart";
import { BestInCategoryChart }         from "./best-in-category-chart";
import { NumberOneRecommendationChart }from "./number-one-recommendation-chart";
import { InsightActionButton }         from "../insight-action-button";
import { AskUpmodeBar }                from "../antenna/ask-upmode-bar";
import { PerformanceSeriesView }       from "./series/performance-series-view";
import { OutcomesView }                from "./outcomes/outcomes-view";
import { PerformanceKpiStrip }         from "./series/performance-kpi-strip";
import { useAnalyticsOverviewData }    from "@/app/hooks/analytics/useAnalyticsOverviewData";
import { getActiveBrand, extractSelectedModels, fetchScheduleConfig } from "@/app/services/analytics/analytics-overview-api";
import { Button } from "../kit";

interface AnalyticsOverviewProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?:    (collapsed: boolean) => void;
  antennaData?:        any;
  brandName?:          string;
  selectedModels?:     string[];
  onNavigate?:         (page: string) => void;
}

type SectionKey = "business-metrics" | "prompts-tracking" | "ai-behavior" | "strategy-insights";

export function AnalyticsOverview({
  isSidebarCollapsed = false,
  onSidebarToggle,
  antennaData,
  brandName: propBrandName,
  selectedModels: propSelectedModels,
  onNavigate,
}: AnalyticsOverviewProps) {
  const [expandedInsight,  setExpandedInsight]  = useState<string | null>(null);
  // Setter dropped with the briefing tab (pulled 7/28, see below): nothing
  // changes this now, so the explorer branch is the only reachable view.
  const [activeTab] = useState<"explorer" | "briefing">("explorer");
  const [expandedSection,  setExpandedSection]  = useState<SectionKey | null>("business-metrics");
  const [scheduleHelpOpen, setScheduleHelpOpen] = useState(false);

  // Next scheduled measurement date, used to draw the forward projection on the
  // longitudinal charts. Real value from the schedule; null until loaded.
  const [nextRunAt, setNextRunAt] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchScheduleConfig()
      .then(cfg => { if (!cancelled) setNextRunAt(cfg?.next_run_at ?? null); })
      .catch(() => { if (!cancelled) setNextRunAt(null); });
    return () => { cancelled = true; };
  }, []);

  // AI sidebar doorway: the Ask Upmode bar and chips open the assistant with
  // the question prefilled (same mechanism as Brand Monitoring). It never
  // answers inline. Topic clears shortly after so re-asking the same thing
  // re-triggers the sidebar.
  const [aiTopic, setAiTopic] = useState<string | undefined>(undefined);
  const handleOpenAIChat = (topic?: string) => {
    setAiTopic(topic);
    if (isSidebarCollapsed && onSidebarToggle) onSidebarToggle(false);
  };
  useEffect(() => {
    if (!aiTopic) return;
    const t = setTimeout(() => setAiTopic(undefined), 500);
    return () => clearTimeout(t);
  }, [aiTopic]);

  const activeBrand      = propBrandName || getActiveBrand();
  const monitoringModels = extractSelectedModels(antennaData, propSelectedModels);

  const { promptTracking, aiInsights: rawInsights, isLoading, error } =
    useAnalyticsOverviewData(antennaData);

  const aiInsights = (rawInsights || []).map((insight) => ({
    ...insight,
    iconColor:
      insight.type === "success"       ? "var(--success-text)"
      : insight.type === "warning"     ? "var(--warning-text)"
      : insight.type === "achievement" ? "var(--chart-3)"
      : "var(--signal-blue)",
  }));

  const contentMarginClass = isSidebarCollapsed ? "lg:mr-12" : "lg:mr-[380px]"
  const sidebarClass = `flex-1 min-w-0 transition-all duration-300 ${contentMarginClass}`;

  // Opt-in: the new contract-driven longitudinal view (lenses + dimension
  // toggle, fed by the validated series envelope). Off by default so the shipped
  // chart stack remains the production path until the backend series endpoint is
  // live. Enable locally with VITE_PERF_SERIES_UI=1.
  const perfSeriesUI = String(import.meta.env?.VITE_PERF_SERIES_UI ?? "") === "1";

  const goToInsights = () => onNavigate?.("report-generator");
  const chartProps   = {
    antennaData,
    selectedModels: monitoringModels,
    brandName: activeBrand || undefined,
    onNavigateToInsights: goToInsights,
    projectionDate: nextRunAt,
  };

  const Header = () => (
    <ModuleHeader
      page="Performance"
      icon={BarChart3}
      title="Progress, proven."
      description="The funnel over time: every read, every content move, and how recommendation gains line up with your own demand data."
      actionsSlot={
        activeBrand ? (
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setScheduleHelpOpen(o => !o)}
              className="inline-flex items-center"
              style={{ gap: 12, height: 40, padding: "0 16px", borderRadius: 8, background: "var(--card)", border: "1px solid var(--border)", cursor: "pointer" }}
              title="Automated scheduling is coming soon"
            >
              <Clock className="w-4 h-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
              <span style={{ fontSize: 14, color: "var(--muted-foreground)" }}>Analytics schedule</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--infrared-coral)", border: "1px solid var(--infrared-coral)", borderRadius: 9999, padding: "1px 8px" }}>
                Coming soon
              </span>
            </button>
            {scheduleHelpOpen && (
              <div
                className="absolute z-50"
                style={{ top: "calc(100% + 8px)", right: 0, width: 244, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 14px 34px -14px rgba(13,13,15,.3)", padding: "12px 14px" }}
              >
                <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
                  Automated measurement scheduling is coming soon. For now, measurement runs are managed for you.
                </p>
              </div>
            )}
          </div>
        ) : undefined
      }
    />
  );

  if (!isLoading && error?.message === "NO_BRAND") {
    return (
      <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
        <div className={sidebarClass}>
          {Header()}
          <div className="flex items-center justify-center px-4" style={{ minHeight: "300px" }}>
            <div className="text-center max-w-sm">
              <BarChart3 className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--muted-foreground)" }} />
              <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", marginBottom: "8px" }}>
                No brand selected
              </h3>
              <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)", lineHeight: "1.6" }}>
                Go to Brand Search, run a brand analysis, then return here to see your analytics.
              </p>
            </div>
          </div>
        </div>
        <div className="hidden lg:block">
          <AIAssistantSidebar phase="analytics" isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} initialTopic={aiTopic} />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
        <div className={sidebarClass}>
          {Header()}
          <div className="flex items-center justify-center px-4" style={{ minHeight: "280px" }}>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full animate-pulse" style={{ backgroundColor: "var(--alpha-teal)" }} />
              <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>Loading analytics…</p>
            </div>
          </div>
        </div>
        <div className="hidden lg:block">
          <AIAssistantSidebar phase="analytics" isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} initialTopic={aiTopic} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
      <div className={sidebarClass}>
        {Header()}

        {/* Headline KPI strip at the very top so the numbers are the first
            thing scanned on arrival, above the Research Analyst card. */}
        {perfSeriesUI && <PerformanceKpiStrip brandName={activeBrand || undefined} />}

        {/* Ask Upmode: conversational entry to the AI sidebar.
            A doorway: the bar and the suggestion chips open the assistant with
            the question prefilled (handleOpenAIChat); they never answer inline.
            Tabs (Ask questions / Weekly briefing) keep the existing state. */}
        <div
          className="border-b"
          style={{
            position: "relative",
            overflow: "hidden",
            padding: "18px clamp(16px, 5vw, 56px)",
            borderColor: "var(--border)",
            background: "linear-gradient(135deg, var(--spectral-violet-10), transparent 58%)",
          }}
        >
          <div className="flex items-start" style={{ gap: 13, marginBottom: 18 }}>
            <div className="min-w-0">
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                  color: "var(--spectral-violet)",
                  marginBottom: 3,
                }}
              >
                Research Analyst
              </div>
              <h3 style={{ fontSize: 21, fontWeight: 600, letterSpacing: "-.01em", color: "var(--foreground)", lineHeight: 1.2 }}>
                Ask anything about your performance data.
              </h3>
            </div>
            {/* Tab toggle pulled 7/28: the Weekly briefing view isn't ready for
                clients, and a single remaining tab is dead chrome. activeTab
                stays "explorer" (its default) so the briefing branch below is
                unreachable but intact for when the briefing comes back. */}
          </div>

          {activeTab === "explorer" ? (
            <AskUpmodeBar
              brandName={activeBrand || undefined}
              onAsk={handleOpenAIChat}
              placeholder={`Ask anything about how AI sees ${activeBrand || "your brand"}: "How do models differ?"`}
              starters={[
                { label: "How has perception changed over time?", query: (b) => `How has AI perception of ${b} changed over time?` },
                { label: "What's trending up?", query: (b) => `Which of ${b}'s performance metrics are trending up, and why?` },
                { label: "What's worth watching?", query: (b) => `In ${b}'s performance data, what is worth watching right now?` },
              ]}
            />
          ) : (
            <div className="pt-1">
              <h4
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-weight-semibold)",
                  marginBottom: "16px",
                }}
              >
                Here's what I found in your data
              </h4>

              {aiInsights.length === 0 ? (
                <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>
                  Insights will appear here once your first analytics snapshot has been generated.
                </p>
              ) : (
                <div className="space-y-2 mb-5 md:mb-6">
                  {aiInsights.map((insight) => {
                    const isExpanded = expandedInsight === insight.id;

                    const insightContent = [
                      insight.subtitle,
                      insight.details,
                      insight.aiReasoning ? `AI Reasoning: ${insight.aiReasoning}` : null,
                    ].filter(Boolean) as string[];

                    const insightTitle =
                      [insight.metric, insight.title].filter(Boolean).join(" ") ||
                      "Analytics Insight";

                    return (
                      <div
                        key={insight.id}
                        className="rounded-lg overflow-hidden"
                        style={{
                          borderLeft: `3px solid ${insight.iconColor}`,
                          border: isExpanded ? "1px solid var(--border)" : "1px solid transparent",
                          borderLeftWidth: "3px",
                          borderLeftColor: insight.iconColor,
                        }}
                      >
                        <div
                          onClick={() => setExpandedInsight(isExpanded ? null : insight.id)}
                          className="flex items-start gap-2 px-3 py-2 cursor-pointer"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                style={{
                                  fontSize: "var(--text-base)",
                                  fontWeight: "var(--font-weight-semibold)",
                                  color: insight.iconColor,
                                }}
                              >
                                {insight.metric}
                              </span>
                              <span
                                style={{
                                  fontSize: "var(--text-base)",
                                  fontWeight: "var(--font-weight-semibold)",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {insight.title}
                              </span>
                            </div>
                            {!isExpanded && (
                              <p
                                style={{
                                  fontSize: "var(--text-sm)",
                                  color: "var(--muted-foreground)",
                                  marginTop: "4px",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {insight.subtitle}
                              </p>
                            )}
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 shrink-0 transition-transform mt-0.5 ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                            style={{ color: "var(--muted-foreground)" }}
                          />
                        </div>

                        {isExpanded && (
                          <div className="px-3 pb-3 space-y-3">
                            <p
                              style={{
                                fontSize: "var(--text-xs)",
                                color: "var(--muted-foreground)",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {insight.subtitle}
                            </p>
                            <p
                              style={{
                                fontSize: "var(--text-sm)",
                                lineHeight: "1.5",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {insight.details}
                            </p>

                            <div
                              className="p-2.5 rounded-lg"
                              style={{
                                backgroundColor: "var(--spectral-violet-5)",
                                border: "1px solid var(--spectral-violet-12)",
                              }}
                            >
                              <div className="flex items-start gap-2 mb-1.5">
                                <Sparkles
                                  className="w-3.5 h-3.5 shrink-0 mt-0.5"
                                  style={{ color: "var(--spectral-violet)" }}
                                />
                                <span
                                  style={{
                                    fontSize: "var(--text-xs)",
                                    fontWeight: "var(--font-weight-semibold)",
                                    color: "var(--spectral-violet)",
                                  }}
                                >
                                  AI Reasoning:
                                </span>
                              </div>
                              <p
                                style={{
                                  fontSize: "var(--text-xs)",
                                  lineHeight: "1.4",
                                  paddingLeft: "20px",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {insight.aiReasoning}
                              </p>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
                              <span
                                style={{
                                  fontSize: "var(--text-xs)",
                                  color: "var(--muted-foreground)",
                                }}
                              >
                                Save this insight to use in report generation
                              </span>
                              <InsightActionButton
                                title={insightTitle}
                                content={insightContent}
                                context={{
                                  module: "analytics",
                                  stageName: "Performance Analytics",
                                  timestamp: new Date(),
                                  accentColor: insight.iconColor,
                                }}
                                brandName={activeBrand || undefined}
                                onNavigateToInsights={goToInsights}
                                size="sm"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {aiInsights.length > 0 && (
                <Button
                  onClick={goToInsights}
                  className="w-full sm:w-auto"
                  icon={Sparkles}
                >Go to Insights &amp; Reports
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Main section stack */}
        <div className="shell-page shell-page--stack">

          {perfSeriesUI ? (
            // New view: flat charts. The Topline KPI strip sits above the page,
            // so there is no Business Metrics accordion and no coming-soon
            // placeholder sections here.
            <>
              <PerformanceSeriesView
                brandName={activeBrand || undefined}
                projectionDate={nextRunAt}
                onNavigateToInsights={goToInsights}
              />
              {/* Outcomes: recommendation movement set against demand. The
                  recommendation series is real where the client has the history;
                  the demand series are illustrative and the view says so. */}
              <OutcomesView brandName={activeBrand || undefined} />
            </>
          ) : (
          <>
          {/* Business Metrics */}
          <section
            className="border"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--card)", borderRadius: 14 }}
          >
            <div
              className="px-4 md:px-6 py-4 cursor-pointer flex items-center justify-between gap-3"
              onClick={() =>
                setExpandedSection(expandedSection === "business-metrics" ? null : "business-metrics")
              }
              style={{
                borderBottom: expandedSection === "business-metrics" ? "1px solid var(--border)" : "none",
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <BarChart3 className="w-5 h-5 flex-shrink-0" style={{ color: "var(--alpha-teal)" }} />
                  <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>
                    Business metrics
                  </h3>
                </div>
                <p
                  style={{
                    color: "var(--muted-foreground)",
                    fontSize: "var(--text-sm)",
                    paddingLeft: "32px",
                  }}
                >
                  Core performance metrics tracked over time. Each AI model is queried independently.
                </p>
              </div>
              <ChevronDown
                className={`w-5 h-5 flex-shrink-0 transition-transform ${
                  expandedSection === "business-metrics" ? "rotate-180" : ""
                }`}
                style={{ color: "var(--muted-foreground)" }}
              />
            </div>

            {expandedSection === "business-metrics" && (
              <div className="p-4 md:p-6 space-y-6 md:space-y-8">
                {promptTracking && (
                  <div className="grid grid-cols-2 xl:grid-cols-4" style={{ gap: 14 }}>
                    {[
                      { label: "Your AI Index",          value: `${promptTracking.sentimentScore}`,         accent: "#12B76A", trend: promptTracking.trendValue },
                      { label: "Mention Rate",           value: `${promptTracking.shareOfVoice}%`,          accent: "#FF6D66", trend: promptTracking.shareOfVoiceTrend },
                      { label: "Top 5 Rate",             value: `${promptTracking.top5Rate}%`,              accent: "#7E5FE0", trend: promptTracking.top5Trend },
                      { label: "Primary Recommendation", value: `${promptTracking.avgRecommendationRate}%`, accent: "#FFD600", trend: promptTracking.primaryRecTrend },
                    ].map((kpi) => {
                      const hasTrend = kpi.trend != null && kpi.trend !== "";
                      const down = hasTrend && String(kpi.trend).trim().startsWith("-");
                      const trendColor = down ? "var(--infrared-coral)" : "#04938F";
                      return (
                        <div
                          key={kpi.label}
                          style={{
                            position: "relative",
                            overflow: "hidden",
                            background: "var(--card)",
                            border: "1px solid var(--border)",
                            borderRadius: 12,
                            padding: 18,
                          }}
                        >
                          <span style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: kpi.accent }} />
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              letterSpacing: ".04em",
                              textTransform: "uppercase",
                              color: "var(--muted-foreground)",
                              marginBottom: 10,
                            }}
                          >
                            {kpi.label}
                          </div>
                          <div
                            style={{
                              fontSize: 34,
                              fontWeight: 800,
                              letterSpacing: "-.02em",
                              color: "var(--foreground)",
                              fontVariantNumeric: "tabular-nums",
                              lineHeight: 1.1,
                              marginBottom: 6,
                            }}
                          >
                            {kpi.value}
                          </div>
                          <div className="flex items-center" style={{ gap: 4 }}>
                            {hasTrend ? (
                              <>
                                <TrendingUp style={{ width: 13, height: 13, color: trendColor }} />
                                <span style={{ fontSize: 13, color: trendColor }}>{kpi.trend}</span>
                              </>
                            ) : (
                              <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
                                {promptTracking.latestIsNewBaseline && kpi.label !== "Your AI Index"
                                  ? "New baseline"
                                  : "First measurement"}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Latest run incomplete: the funnel KPIs above are over its
                    answered calls only. Nothing renders for a complete run. */}
                <IncompleteRunNotice completeness={promptTracking?.latestCompleteness} />
                {/* Latest read is a series rebaseline's new baseline: what
                    changed, and why. Nothing renders otherwise. */}
                {promptTracking?.latestIsNewBaseline && (
                  <RebaselineNotice rebaseline={promptTracking.latestRebaseline} newBaseline compact />
                )}
                <div style={{ height: "1px", backgroundColor: "var(--border)" }} />
                <PerceptionScoreChart {...chartProps} />
                <div style={{ height: "1px", backgroundColor: "var(--border)" }} />
                <ReferralConversationsChart {...chartProps} />
                <div style={{ height: "1px", backgroundColor: "var(--border)" }} />
                <BestInCategoryChart {...chartProps} />
                <div style={{ height: "24px" }} />
                <NumberOneRecommendationChart {...chartProps} />
              </div>
            )}
          </section>

          </>
          )}
        </div>
      </div>

      <div className="hidden lg:block">
        <AIAssistantSidebar phase="analytics" isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} initialTopic={aiTopic} />
      </div>
    </div>
  );
}